import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import { Box, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from "@mui/material";
import { useState } from "react";
import { AvailabilityByTimespanQuery } from "~/graphql/__generated__/graphql"
import ProviderFilterChips, { useDefaultFilters, ProviderFilters } from "./ProviderFilterChips";
import { parseAvailability } from "@caredna-marlin/utils/client/availability";
import ProviderSelectableTableRow from "./ProviderSelectableTableRow";
import { fromPairs, groupBy, keyBy } from "lodash";
import { filterProviderRows, NoProviders } from './ProviderTable';
import { ProviderTableRowSkeleton } from './ProviderTableRow';

type Props = {
  data: AvailabilityByTimespanQuery | undefined,
  excludeProviders: number[],
  highlightAvailability?: number[],
  selectedProviders: Record<string, boolean>,
  setSelectedProviders: (selected: Record<string, boolean>) => void,
}

const setAll = (toSet: {id: number}[], value: boolean, initialSelected: Record<string, boolean>) => {
  return toSet.reduce((selected, toSetKey) => {
    selected[toSetKey.id] = value;
    return selected;
  }, {...initialSelected});
}

const ProviderSelectTable = ({ data, excludeProviders, highlightAvailability, selectedProviders, setSelectedProviders }: Props) => {
  const [filters, setFilters] = useState<ProviderFilters>(useDefaultFilters());

  const availabilityByProvider = groupBy(data?.availability.map(a => parseAvailability(a)) || [], "providerId");
  const teams = keyBy(data?.team || [], "id");

  const filteredProviders = filterProviderRows(data?.provider || [], availabilityByProvider, filters, excludeProviders);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const allVisibleSelected = filteredProviders.every(p => selectedProviders[p.id]);
  const allVisibleUnselected = filteredProviders.every(p => !selectedProviders[p.id]);

  return <Box>
    <ProviderFilterChips
      values={filters}
      setValues={setFilters}
      teams={teams}
    />

    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell colSpan={2} sx={{minWidth: "15em"}}>
              Provider Name
              <Tooltip title="Expand All">
                <IconButton size="small" sx={{ ml: 1 }} onClick={() => setExpanded(fromPairs(filteredProviders.map(p => [p.id, true])))}>
                  <UnfoldMoreIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Collapse All">
                <IconButton size="small" onClick={() => setExpanded({})}>
                   <UnfoldLessIcon />
                </IconButton>
              </Tooltip>
            </TableCell>
            <TableCell sx={{ fontWeight: 600, minWidth: 120, whiteSpace: 'nowrap' }}>Provider Type</TableCell>
            <TableCell>Address</TableCell>
            <TableCell>Service Lines</TableCell>
            <TableCell>Teams</TableCell>
          </TableRow>

          <TableRow>
            <TableCell colSpan={6} sx={{ p: 0, button: { mr: 2 } }}>
              <Button
                size="small"
                disabled={allVisibleSelected}
                onClick={() => setSelectedProviders(setAll(filteredProviders, true, selectedProviders))}
              >Select All</Button>
              <Button
                size="small"
                disabled={allVisibleUnselected}
                onClick={() => setSelectedProviders(setAll(filteredProviders, false, selectedProviders))}
              >Unselect All</Button>
            </TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {!data ? (
            <ProviderTableRowSkeleton />
          ) : filteredProviders.length === 0 ? (
            <NoProviders count={data.provider.length} />
          ) : (
            filteredProviders.map((p, i) => <ProviderSelectableTableRow
              key={p.id}
              index={i}
              availability={availabilityByProvider[p.id] || []}
              highlightAvailability={highlightAvailability}
              provider={p}
              teams={teams}
              expanded={expanded[p.id]}
              setExpanded={(e) => setExpanded({ ...expanded, [p.id]: e })}

              selected={selectedProviders[p.id] || false}
              setSelected={s => setSelectedProviders({ ...selectedProviders, [p.id]: s })}
            />)
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Box>;
}

export default ProviderSelectTable
